import axios, { AxiosInstance } from "axios";
import { store } from "../../state";
import settings from "../../config/settings";
import LoginService from "../loginService";
import { CustomParam, Dictionary } from "../../types/types";

interface Dic {
  [key: string]: string | number;
}

interface CallDataInterface {
  name: string;
  value: any;
}

export enum Operations {
  EQUAL = "eq",
  IN = "in",
  LOWER = "lt",
  GREATER = "gt",
  NONE = "none",
}
export interface ParamData {
  operation: Operations;
  value: string | number;
}

export interface ParamsData {
  [key: string]: ParamData;
}

export interface HeadersInterface extends Array<CallDataInterface> {}
export interface QueryParamsInterface extends Array<CallDataInterface> {}
export interface BodyParamsInterface extends Array<CallDataInterface> {}

export default class API {
  private static isLoginInProgress = false;

  public static setLoginInProgress = (inProgress: boolean) => {
    this.isLoginInProgress = inProgress;
  };

  public static handleLogout = () => {
    // Don't auto-logout if login is in progress to prevent race conditions
    if (this.isLoginInProgress) {
      console.log("🚫 API: Prevented logout during login process");
      return;
    }
    console.log("🚪 API: Triggering logout and redirect to home");
    const { setLogout } = LoginService();
    setLogout();
  };

  private static classInstance?: AxiosInstance;
  private constructor() {}

  public static getNonce = () => {
    return Date.now() + Math.floor(Math.random() * (10000 + 1) + 1000);
  };

  public static defaultHeaders = (): any => {
    return {
      "X-NONCE": this.getNonce(),
    };
  };

  private static configAxios = (instance: AxiosInstance) => {
    instance.interceptors.request.use(
      (config) => {
        const {
          login: { access_token },
        } = store.getState();
        if (/* !config?.url?.includes('http') &&  */ !!access_token) {
          config.headers["Authorization"] = "Bearer " + access_token;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      function (error) {
        if (error && error.response && error.response.status === 401) {
          //revokeUserAuthorization()
        }
        return Promise.reject(error);
      }
    );
  };

  public static getInstance() {
    if (!this.classInstance) {
      this.classInstance = axios.create({
        baseURL: settings.apiroot,
        timeout: 60000,
        headers: { "Content-Type": "application/json" },
      });
      this.configAxios(this.classInstance);
    }
    return this.classInstance;
  }
}

export const buildParamsDataOld = (params: Dictionary<string | number>) => {
  let paramsData: ParamsData = {};
  Object.keys(params).forEach((paramKey) => {
    let data: ParamData = {
      operation: Operations.EQUAL,
      value: params[paramKey],
    };
    paramsData[paramKey] = data;
  });
  return paramsData;
};

export const buildParamsData = (params: CustomParam<string | number>) => {
  let paramsData: ParamsData = {};
  Object.keys(params).forEach((paramKey) => {
    let data: ParamData = {
      operation: params[paramKey].operation,
      value: params[paramKey].value,
    };
    paramsData[paramKey] = data;
  });
  return paramsData;
};

export const buildParams = (params: ParamsData) => {
  const exceptions = ["q", "page", "order_by", "order_direction", "perPage"];
  let textParams: Array<string> = [];
  Object.keys(params).forEach((pkey) => {
    if (exceptions.includes(String(pkey))) {
      textParams.push(String(pkey) + "=" + String(params[pkey].value));
    } else {
      if (params[pkey].operation === Operations.NONE) {
        textParams.push(String(pkey) + "=" + String(params[pkey].value));
      } else {
        textParams.push(
          String(pkey) +
            "[" +
            params[pkey].operation +
            "]=" +
            String(params[pkey].value)
        );
      }
    }
  });

  return textParams.join("&");
};

export function convertInterfaceToBodyParams<T>(data: T): BodyParamsInterface {
  const bodyParams: BodyParamsInterface = [];

  for (const key in data) {
    if (
      Object.prototype.hasOwnProperty.call(data, key) &&
      data[key] !== undefined &&
      data[key] !== null
    ) {
      bodyParams.push({
        name: key,
        value: data[key],
      });
    }
  }

  return bodyParams;
}

export const postCall = async (
  url: string,
  headerdata: HeadersInterface | undefined,
  params: QueryParamsInterface | undefined,
  body: BodyParamsInterface | undefined,
  skipLogoutOn401?: boolean
) => {
  let headers = API.defaultHeaders();
  if (headerdata) {
    (Object.keys(headerdata) as (keyof typeof headerdata)[]).forEach(
      (key, index) => {
        headers[headerdata[index].name] = headerdata[index].value;
      }
    );
  }

  let qParams: URLSearchParams | undefined;
  qParams = undefined;
  if (params !== undefined) {
    let paramsData = new URLSearchParams();
    (Object.keys(params) as (keyof typeof params)[]).forEach((key, index) => {
      paramsData.append(params[index].name, params[index].value);
    });
    qParams = paramsData;
  }

  let callBody: Dic = {};
  if (body) {
    (Object.keys(body) as (keyof typeof body)[]).forEach((key, index) => {
      callBody[body[index].name] = body[index].value;
    });
  }

  const response = await API.getInstance().post(
    url,
    qParams !== undefined ? qParams : callBody,
    { headers: headers }
  );

  // Only trigger logout for 401 if we have an access token (user is supposed to be logged in)
  if (response.status === 401 && !skipLogoutOn401) {
    const {
      login: { access_token },
    } = store.getState();
    if (access_token && access_token !== "") {
      console.log("🚪 API: 401 with valid token, triggering logout");
      API.handleLogout();
    } else {
      console.log("🔓 API: 401 without token (expected for public endpoint)");
    }
  }

  return response;
};

export const putCall = async (
  url: string,
  headerdata: HeadersInterface | undefined,
  params: QueryParamsInterface | undefined,
  body: BodyParamsInterface | undefined,
  skipLogoutOn401?: boolean
) => {
  let headers = API.defaultHeaders();
  if (headerdata) {
    (Object.keys(headerdata) as (keyof typeof headerdata)[]).forEach(
      (key, index) => {
        headers[headerdata[index].name] = headerdata[index].value;
      }
    );
  }

  let qParams: URLSearchParams | undefined;
  qParams = undefined;
  if (params !== undefined) {
    let paramsData = new URLSearchParams();
    (Object.keys(params) as (keyof typeof params)[]).forEach((key, index) => {
      paramsData.append(params[index].name, params[index].value);
    });
    qParams = paramsData;
  }

  let callBody: Dic = {};
  if (body) {
    (Object.keys(body) as (keyof typeof body)[]).forEach((key, index) => {
      callBody[body[index].name] = body[index].value;
    });
  }

  const response = await API.getInstance().put(
    url,
    qParams !== undefined ? qParams : callBody,
    { headers: headers }
  );

  // Only trigger logout for 401 if we have an access token (user is supposed to be logged in)
  if (response.status === 401 && !skipLogoutOn401) {
    const {
      login: { access_token },
    } = store.getState();
    if (access_token && access_token !== "") {
      console.log("🚪 API: 401 with valid token, triggering logout");
      API.handleLogout();
    } else {
      console.log("🔓 API: 401 without token (expected for public endpoint)");
    }
  }

  return response;
};

export const getCall = async (
  url: string,
  headerdata: HeadersInterface | undefined,
  params: string | undefined,
  skipLogoutOn401?: boolean
) => {
  let headers = API.defaultHeaders();
  if (headerdata) {
    (Object.keys(headerdata) as (keyof typeof headerdata)[]).forEach(
      (key, index) => {
        headers[headerdata[index].name] = headerdata[index].value;
      }
    );
  }

  let newUrl = url;
  if (params) {
    newUrl = newUrl + "?" + params;
  }

  const response = await API.getInstance().get(newUrl, { headers: headers });

  // Only trigger logout for 401 if we have an access token (user is supposed to be logged in)
  if (response.status === 401 && !skipLogoutOn401) {
    const {
      login: { access_token },
    } = store.getState();
    if (access_token && access_token !== "") {
      console.log("🚪 API: 401 with valid token, triggering logout");
      API.handleLogout();
    } else {
      console.log("🔓 API: 401 without token (expected for public endpoint)");
    }
  }

  return response;
};

export const patchCall = async (
  url: string,
  headerdata: HeadersInterface | undefined,
  params: QueryParamsInterface | undefined,
  body: BodyParamsInterface | undefined,
  skipLogoutOn401?: boolean
) => {
  let headers = API.defaultHeaders();
  if (headerdata) {
    (Object.keys(headerdata) as (keyof typeof headerdata)[]).forEach(
      (key, index) => {
        headers[headerdata[index].name] = headerdata[index].value;
      }
    );
  }

  let qParams: URLSearchParams | undefined;
  qParams = undefined;
  if (params !== undefined) {
    let paramsData = new URLSearchParams();
    (Object.keys(params) as (keyof typeof params)[]).forEach((key, index) => {
      paramsData.append(params[index].name, params[index].value);
    });
    qParams = paramsData;
  }

  let callBody: Dic = {};
  if (body) {
    (Object.keys(body) as (keyof typeof body)[]).forEach((key, index) => {
      callBody[body[index].name] = body[index].value;
    });
  }

  const response = await API.getInstance().patch(
    url,
    qParams !== undefined ? qParams : callBody,
    { headers: headers }
  );

  // Only trigger logout for 401 if we have an access token (user is supposed to be logged in)
  if (response.status === 401 && !skipLogoutOn401) {
    const {
      login: { access_token },
    } = store.getState();
    if (access_token && access_token !== "") {
      console.log("🚪 API: 401 with valid token, triggering logout");
      API.handleLogout();
    } else {
      console.log("🔓 API: 401 without token (expected for public endpoint)");
    }
  }

  return response;
};

export const deleteCall = async (
  url: string,
  headerdata: HeadersInterface | undefined,
  skipLogoutOn401?: boolean
) => {
  let headers = API.defaultHeaders();
  if (headerdata) {
    (Object.keys(headerdata) as (keyof typeof headerdata)[]).forEach(
      (key, index) => {
        headers[headerdata[index].name] = headerdata[index].value;
      }
    );
  }

  const response = await API.getInstance().delete(url, { headers: headers });

  // Only trigger logout for 401 if we have an access token (user is supposed to be logged in)
  if (response.status === 401 && !skipLogoutOn401) {
    const {
      login: { access_token },
    } = store.getState();
    if (access_token && access_token !== "") {
      console.log("🚪 API: 401 with valid token, triggering logout");
      API.handleLogout();
    } else {
      console.log("🔓 API: 401 without token (expected for public endpoint)");
    }
  }

  return response;
};
